import { useMutation, useQuery } from '@tanstack/react-query';

import { RecordsWithTimestamp, SearchHistoryEntry } from '@app/components';
import {
  queryKeys,
  RecentActivityInstanceDto,
  useFormattedSearches,
  usePatronSession,
  UserSearchDto,
} from '@app/hooks';
import { makeRequest } from '@app/utils';

export type SavedInstancesDto = {
  savedInstances: RecentActivityInstanceDto[];
};

export type UserSearchesDto = {
  savedSearches: UserSearchDto[];
};

export interface SavedInstancesCompact {
  ids: Record<string, string>;
}

const savedActivityLimit = 500;

export const useSavedInstancesFetch = () => {
  const { externalSystemId } = usePatronSession();

  const url = `/opac-patron/account/${externalSystemId}/saved-instances`;

  return useQuery<SavedInstancesDto, unknown, RecordsWithTimestamp[]>(
    ['savedInstances', externalSystemId],
    () =>
      makeRequest<SavedInstancesDto>({
        url,
        params: { limit: savedActivityLimit },
      }),
    {
      enabled: Boolean(externalSystemId),
      select: ({ savedInstances }) =>
        savedInstances.map(({ instanceId, metadata, id }) => ({
          instanceId,
          timestamp: metadata.createdDate,
          id,
        })),
    }
  );
};

export const useSavedInstancesCompact = () => {
  const { externalSystemId } = usePatronSession();

  const url = `/opac-patron/account/${externalSystemId}/saved-instances/compact`;

  return useQuery<SavedInstancesCompact, unknown, SavedInstancesCompact['ids']>(
    [queryKeys.savedInstancesCompact, externalSystemId],
    () =>
      makeRequest({
        url,
        params: { limit: savedActivityLimit },
      }),
    {
      select: ({ ids }) => ids,
      enabled: Boolean(externalSystemId),
    }
  );
};

export const useSavedSearchesFetch = () => {
  const { externalSystemId } = usePatronSession();
  const formatSearches = useFormattedSearches();

  const url = `/opac-patron/account/${externalSystemId}/searches`;

  return useQuery<UserSearchesDto, unknown, SearchHistoryEntry[]>(
    [queryKeys.savedSearches, externalSystemId],
    () =>
      makeRequest<UserSearchesDto>({
        url,
        params: { limit: savedActivityLimit },
      }),
    {
      enabled: Boolean(externalSystemId),
      select: ({ savedSearches }) => formatSearches(savedSearches),
    }
  );
};

const createInstancesUrl = (externalSystemId: string) =>
  `/opac-patron/account/${externalSystemId}/saved-instances`;

const createSearchesUrl = (externalSystemId: string) =>
  `/opac-patron/account/${externalSystemId}/searches`;

interface UseSaveSavedInstanceMutationProps {
  savedInstances: { instanceId: string }[];
  systemId?: string;
}

export const useSaveSavedInstancesMutation = () => {
  const { externalSystemId = '' } = usePatronSession();

  return useMutation(
    ({ savedInstances, systemId = '' }: UseSaveSavedInstanceMutationProps) =>
      makeRequest({
        url: createInstancesUrl(systemId ?? externalSystemId),
        method: 'POST',
        data: savedInstances,
      })
  );
};

interface UseSaveSavedSearchesMutationProps {
  searches: UserSearchDto[];
  systemId?: string;
}

export const useSaveSavedSearchesMutation = () => {
  const { externalSystemId } = usePatronSession();

  return useMutation(
    ({ searches, systemId }: UseSaveSavedSearchesMutationProps) =>
      makeRequest({
        url: createSearchesUrl(systemId ?? (externalSystemId as string)),
        method: 'POST',
        data: searches,
      })
  );
};
